import { Clock, MapPin, Calendar, Users, Music, Heart } from "lucide-react";

const classData = {
  name: "Weekly Afro-Cuban Dance Class",
  description: "Diasporic dance as a tool for connection and somatic movement. Immerse yourself in the vibrant rhythms of Afro-Cuban dance every Wednesday evening. This weekly class blends traditional movement with somatic awareness, building strength, flexibility, and cultural connection.",
  schedule: "Every Wednesday",
  time: "7:00 PM - 8:30 PM",
  duration: "1 hr 30 min",
  location: "BrwnBox, 557 Nassau St, Orange, NJ 07050",
  instructor: "Coral",
  image: "https://customer-assets.emergentagent.com/job_dozaguas-new/artifacts/vilaa0a7_Afro-Cuban%20Dance%20%281%29%20%281%29.png",
  highlights: [
    "Traditional Afro-Cuban rhythms & movement",
    "Somatic awareness integration",
    "All levels welcome",
    "Diasporic dance for connection"
  ]
};

const DanceClassSection = () => {
  return (
    <section
      data-testid="dance-class-section"
      id="classes"
      className="py-24 md:py-32 bg-[#EBE6DF]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#596B5B] mb-3 font-medium">
            Weekly Class
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-[#1A3626] tracking-tight">
            Afro-Cuban Dance
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Left - Image & Story */}
          <div className="space-y-6">
            <div className="relative overflow-hidden rounded-lg">
              <img
                data-testid="dance-class-image"
                src={classData.image}
                alt={classData.name}
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="px-4 py-1.5 bg-[#CC5543] text-white text-xs uppercase tracking-[0.15em] font-medium rounded-full">
                  New
                </span>
              </div>
            </div>
            <p className="text-sm text-[#596B5B] leading-relaxed">
              {classData.description}
            </p>
            {/* Highlights */}
            <div className="grid grid-cols-2 gap-3">
              {classData.highlights.map((h, i) => (
                <div
                  key={i}
                  data-testid={`class-highlight-${i}`}
                  className="flex items-start gap-2 text-xs text-[#1A3626]"
                >
                  <Heart size={14} className="text-[#CC5543] mt-0.5 flex-shrink-0" />
                  <span>{h}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Booking Card */}
          <div className="flex flex-col">
            <div
              data-testid="booking-card"
              className="bg-[#F9F6F0] border border-[#DCD6CC] rounded-lg p-8 md:p-10 flex-1 flex flex-col"
            >
              <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-[#1A3626] mb-8">
                {classData.name}
              </h3>

              <div className="space-y-5 mb-8">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-[#8C9D88]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#596B5B]">Schedule</p>
                    <p className="text-sm text-[#1A3626] font-medium">{classData.schedule}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-[#8C9D88]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#596B5B]">Time</p>
                    <p className="text-sm text-[#1A3626] font-medium">{classData.time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-[#8C9D88]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#596B5B]">Location</p>
                    <p className="text-sm text-[#1A3626] font-medium">{classData.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users size={18} className="text-[#8C9D88]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#596B5B]">Instructor</p>
                    <p className="text-sm text-[#1A3626] font-medium">{classData.instructor}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Music size={18} className="text-[#8C9D88]" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#596B5B]">Duration</p>
                    <p className="text-sm text-[#1A3626] font-medium">{classData.duration}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#DCD6CC] pt-6 mt-auto">
                <div data-testid="coming-back-soon" className="text-center py-4">
                  <p className="font-['Cormorant_Garamond'] text-2xl text-[#1A3626] mb-2">
                    Coming Back Soon
                  </p>
                  <p className="text-xs text-[#596B5B] leading-relaxed">
                    Stay tuned — follow us on Instagram for updates on the next session
                  </p>
                  <a
                    data-testid="follow-instagram-btn"
                    href="https://www.instagram.com/doz.aguas/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-4 px-8 py-3.5 border border-[#1A3626] text-[#1A3626] text-xs uppercase tracking-[0.2em] font-medium rounded-full hover:bg-[#1A3626] hover:text-white transition-all duration-300"
                  >
                    Follow @doz.aguas
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DanceClassSection;
